/* MotionInterpGL — motion-compensated frame interpolation on WebGL2.
 *
 *   var mi = MotionInterpGL.create(gl, { width, height });
 *   mi.prepare(texA, texB, aWasB);   // motion A→B and B→A for one source pair
 *   mi.render(t, framebuffer|null);  // the frame at 0 < t < 1, width × height
 *   mi.verdict();                    // { agree, cost, unreadable } — a sync, call rarely
 *   mi.dispose();
 * texA / texB: the caller's RGBA textures, any size (sampled in normalised
 * coordinates), LINEAR filtering. `aWasB` — the caller vouches that texA holds
 * exactly the picture the previous prepare() had as texB (it rolled A := B and
 * uploaded nothing into it since): that pyramid is reused. The texture handle
 * alone cannot say it — a lane has two textures and alternates them.
 *
 * WHY (2026-10-22, /video-fps report «до 120 увеличил, а стало лишь 30»): the
 * fast lane made its in-between frames by cross-fading A and B. The file was
 * honestly 112 fps, but every in-between frame showed the A position and a
 * ghost of B — measured against a 120 fps truth, a ×4 cross-fade showed 30
 * distinct motion positions per second, and 80 % of the people who got a file
 * from the tool got it from that lane. This module finds where things moved
 * and draws them where they are at time t.
 *
 * Method (classical, no network): luma pyramid at ≈640×360 → block matching
 * coarse-to-fine (8×8 blocks, full search ±7 at the top) → per-pixel choice
 * among nearby block vectors + one Lucas–Kanade step (sub-pixel) → for each t,
 * per pixel, the vector whose two ends (A at p−t·v, B at p+(1−t)·v) agree
 * best → full-size composite that keeps, of the 4 nearest motion samples, the
 * ones whose ends agree in colour (sharp moving edges).
 *
 * Measured against truth (PSNR of the made-up frames, 10 clips: a Roblox-like
 * 3D scene, game pans with HUD, Xiph 1080p50 footage): this 32.5 dB on
 * average, RIFE 4.26 (the AI lane) 32.2, FFmpeg minterpolate mci 31.0, the
 * old cross-fade 24.6. Cost on a weak iGPU (Intel UHD, 1920×1072, ×4): 7.8 ms
 * of GPU per output frame, against ~5 s for RIFE on a CPU. Every knob below
 * was set by that measurement — see tests/playwright/video-fps-moves-the-motion.mjs.
 */
(function (root) {
'use strict';

var VS = [
'#version 300 es',
'layout(location=0) in vec2 a_pos;',
'void main(){ gl_Position = vec4(a_pos, 0.0, 1.0); }'
].join('\n');

var HEAD = [
'#version 300 es',
'precision highp float;',
'precision highp sampler2D;',
'out vec4 o;',
// luma at a texel-space point (pixel centres at +0.5), bilinear; g_inv is set
// once per fragment — asking textureSize per sample costs an instruction each
'vec2 g_inv;',
'float L(sampler2D t, vec2 p){ return texture(t, p * g_inv).r; }',
// a float field with manual bilinear (float textures may lack linear filtering)
'vec4 F(sampler2D t, vec2 p){',
'  ivec2 s = textureSize(t, 0);',
'  vec2 q = clamp(p - 0.5, vec2(0.0), vec2(s) - 1.0);',
'  ivec2 i0 = ivec2(floor(q)); ivec2 i1 = min(i0 + 1, s - 1); vec2 f = q - vec2(i0);',
'  vec4 a = texelFetch(t, i0, 0), b = texelFetch(t, ivec2(i1.x, i0.y), 0);',
'  vec4 c = texelFetch(t, ivec2(i0.x, i1.y), 0), d = texelFetch(t, i1, 0);',
'  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);',
'}',
''
].join('\n');

// RGBA (any size) → working-size luma: 4 bilinear taps a quarter pixel apart.
var FS_LUMA = HEAD + [
'uniform sampler2D u_src;',
'uniform vec2 u_work;',
'void main(){',
'  vec2 uv = gl_FragCoord.xy / u_work, d = 0.25 / u_work;',
'  vec3 s = texture(u_src, uv + vec2(-d.x, -d.y)).rgb + texture(u_src, uv + vec2(d.x, -d.y)).rgb',
'         + texture(u_src, uv + vec2(-d.x, d.y)).rgb + texture(u_src, uv + vec2(d.x, d.y)).rgb;',
'  o = vec4(dot(s * 0.25, vec3(0.299, 0.587, 0.114)), 0.0, 0.0, 1.0);',
'}'
].join('\n');

// 2× box downsample of a luma level (one bilinear tap at the 2×2 corner).
var FS_HALF = HEAD + [
'uniform sampler2D u_src;',
'void main(){ g_inv = 1.0 / vec2(textureSize(u_src, 0)); o = vec4(L(u_src, gl_FragCoord.xy * 2.0), 0.0, 0.0, 1.0); }'
].join('\n');

// Central-difference gradient of the working-size luma, once per frame.
var FS_GRAD = HEAD + [
'uniform sampler2D u_src;',
'void main(){',
'  ivec2 s = textureSize(u_src, 0); ivec2 p = ivec2(gl_FragCoord.xy);',
'  float l = texelFetch(u_src, clamp(p - ivec2(1, 0), ivec2(0), s - 1), 0).r;',
'  float r = texelFetch(u_src, clamp(p + ivec2(1, 0), ivec2(0), s - 1), 0).r;',
'  float d = texelFetch(u_src, clamp(p - ivec2(0, 1), ivec2(0), s - 1), 0).r;',
'  float u = texelFetch(u_src, clamp(p + ivec2(0, 1), ivec2(0), s - 1), 0).r;',
'  o = vec4((r - l) * 0.5, (u - d) * 0.5, 0.0, 0.0);',
'}'
].join('\n');

// Mean luma of A and B (x, y) from the top of the pyramid, one texel. Every
// match cost subtracts the difference, so a fade or a flash is not read as
// motion (a 3D clip faded to black: 33.7 dB cross-fade → 41.1 with it).
var FS_STATS = HEAD + [
'uniform sampler2D u_a, u_b;',
'void main(){',
'  ivec2 s = textureSize(u_a, 0);',
'  float sa = 0.0, sb = 0.0;',
'  for (int y = 0; y < 64; y++) { if (y >= s.y) break;',
'    for (int x = 0; x < 128; x++) { if (x >= s.x) break;',
'      sa += texelFetch(u_a, ivec2(x, y), 0).r; sb += texelFetch(u_b, ivec2(x, y), 0).r; } }',
'  float n = float(min(s.x, 128) * min(s.y, 64));',
'  o = vec4(sa / n, sb / n, 0.0, 0.0);',
'}'
].join('\n');

// Block matching at one pyramid level; one fragment = one 8×8 block.
// Out: (vx, vy, cost, 0) in this level's pixels, from ref towards oth.
// Candidates: the parent level's 3×3 vectors (×2), its interpolated one and
// zero, then ±1 px descent. BM_TAPS² samples over a 10 px window: 4×4 costs
// the same quality as 6×6 (±0.1 dB on every clip). The descent is not
// optional: one round instead of three lost 2.7–6 dB on fast camera turns.
var FS_BM = HEAD + [
'uniform sampler2D u_ref, u_oth, u_parent, u_gm;',
'uniform int u_coarsest, u_radius;',
'uniform float u_lambda, u_dir;',
'const float B = 8.0;',
'float g_shift;',
'float cost(vec2 c, vec2 v){',
'  float s = 0.0;',
'  for (int j = 0; j < BM_TAPS; j++) for (int i = 0; i < BM_TAPS; i++) {',
'    vec2 q = c + (vec2(float(i), float(j)) - float(BM_TAPS - 1) * 0.5) * (10.0 / float(BM_TAPS - 1));',
'    s += abs(L(u_ref, q) - L(u_oth, q + v) + g_shift);',
'  }',
'  return s * (255.0 / float(BM_TAPS * BM_TAPS));',
'}',
'void main(){',
'  g_inv = 1.0 / vec2(textureSize(u_ref, 0));',
'  vec4 gm = texelFetch(u_gm, ivec2(0), 0);',
'  g_shift = u_dir * (gm.y - gm.x);',
'  vec2 c = floor(gl_FragCoord.xy) * B + B * 0.5;',
'  vec2 best = vec2(0.0), pred = vec2(0.0); float bc;',
'  if (u_coarsest == 1) {',
'    bc = cost(c, best);',
'    for (int y = -u_radius; y <= u_radius; y++) for (int x = -u_radius; x <= u_radius; x++) {',
'      vec2 v = vec2(float(x), float(y));',
'      float k = cost(c, v) + u_lambda * length(v) * 0.25;',
'      if (k < bc) { bc = k; best = v; }',
'    }',
'  } else {',
'    ivec2 ps = textureSize(u_parent, 0);',
'    vec2 pc = c * 0.5 / B;',
'    pred = F(u_parent, pc).xy * 2.0;',
'    bc = cost(c, best) + u_lambda * length(best - pred);',
'    ivec2 pb = ivec2(floor(pc));',
'    for (int dy = -1; dy <= 1; dy++) for (int dx = -1; dx <= 1; dx++) {',
'      vec2 v = texelFetch(u_parent, clamp(pb + ivec2(dx, dy), ivec2(0), ps - 1), 0).xy * 2.0;',
'      float k = cost(c, v) + u_lambda * length(v - pred);',
'      if (k < bc) { bc = k; best = v; }',
'    }',
'    float k0 = cost(c, pred);',
'    if (k0 < bc) { bc = k0; best = pred; }',
'  }',
'  for (int r = 0; r < BM_DESC; r++) {',
'    vec2 b0 = best;',
'    for (int y = -1; y <= 1; y++) for (int x = -1; x <= 1; x++) {',
'      if (x == 0 && y == 0) continue;',
'      vec2 v = b0 + vec2(float(x), float(y));',
'      float k = cost(c, v) + u_lambda * length(v - pred) * (u_coarsest == 1 ? 0.25 : 1.0);',
'      if (k < bc) { bc = k; best = v; }',
'    }',
'    if (best == b0) break;',
'  }',
'  o = vec4(best, bc, 0.0);',
'}'
].join('\n');

// Dense flow at working size: each pixel picks among its block's vector and
// the 4 neighbours' by a small match, then one Lucas–Kanade step on the
// precomputed gradient gives the sub-pixel part. 3×3 taps 2 px apart, five
// candidates and one step measured equal to 5×5 / nine / two (±0.02 dB) at a
// quarter of the cost.
var FS_DENSE = HEAD + [
'uniform sampler2D u_ref, u_oth, u_blocks, u_gm, u_grad;',
'uniform float u_lambda, u_dir;',
'const float B = 8.0;',
'float g_shift;',
'float cost(vec2 p, vec2 v){',
'  float s = 0.0;',
'  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {',
'    vec2 q = p + vec2(float(i), float(j)) * 2.0;',
'    s += abs(L(u_ref, q) - L(u_oth, q + v) + g_shift);',
'  }',
'  return s * (255.0 / 9.0);',
'}',
'void main(){',
'  g_inv = 1.0 / vec2(textureSize(u_ref, 0));',
'  vec4 gm = texelFetch(u_gm, ivec2(0), 0);',
'  g_shift = u_dir * (gm.y - gm.x);',
'  vec2 p = gl_FragCoord.xy;',
'  ivec2 ip = ivec2(p), ls = textureSize(u_ref, 0), bs = textureSize(u_blocks, 0);',
'  vec2 bp = p / B;',
'  vec2 vb = F(u_blocks, bp).xy;',
'  vec2 best = vb; float bc = cost(p, vb);',
'  ivec2 b0 = ivec2(floor(bp));',
'  for (int n = 0; n < 5; n++) {',
'    ivec2 d = n == 0 ? ivec2(0) : (n == 1 ? ivec2(1, 0) : (n == 2 ? ivec2(-1, 0) : (n == 3 ? ivec2(0, 1) : ivec2(0, -1))));',
'    vec2 v = texelFetch(u_blocks, clamp(b0 + d, ivec2(0), bs - 1), 0).xy;',
'    float k = cost(p, v) + u_lambda * length(v - vb);',
'    if (k < bc) { bc = k; best = v; }',
'  }',
'  float gxx = 0.0, gxy = 0.0, gyy = 0.0, bx = 0.0, by = 0.0;',
'  for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {',
'    ivec2 iq = clamp(ip + ivec2(i, j), ivec2(0), ls - 1);',
'    vec2 g = texelFetch(u_grad, iq, 0).xy;',
'    float e = L(u_oth, vec2(iq) + 0.5 + best) - g_shift - texelFetch(u_ref, iq, 0).r;',
'    gxx += g.x * g.x; gxy += g.x * g.y; gyy += g.y * g.y; bx += g.x * e; by += g.y * e;',
'  }',
'  float det = gxx * gyy - gxy * gxy;',
'  if (det > 1e-7) best += clamp(-vec2(gyy * bx - gxy * by, -gxy * bx + gxx * by) / det, vec2(-1.0), vec2(1.0));',
'  o = vec4(best, cost(p, best), 0.0);',
'}'
].join('\n');

// The motion AT TIME t (working size). A candidate v carries content from A
// at p − t·v to B at p + (1−t)·v; the pair of ends that agree best wins.
// Candidates: each flow followed to its fixed point at t (two iterations),
// zero, and the same 4 px to the sides. Reading occlusions one-sided (seen
// only in A / only in B) was built and measured: it lost on every clip, so
// the two-sided match is the only judge. No candidate array: a dynamically
// indexed local array spills to scratch memory on D3D.
var FS_TFIELD = HEAD + [
'uniform sampler2D u_la, u_lb, u_fab, u_fba, u_gm;',
'uniform float u_t;',
'float g_shift, g_bc; vec2 g_best, g_p;',
'float bic(vec2 v){',
'  vec2 a = g_p - u_t * v, b = g_p + (1.0 - u_t) * v;',
'  float s = 0.0;',
'  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {',
'    vec2 d = vec2(float(i), float(j)) * 1.5;',
'    s += abs(L(u_la, a + d) - L(u_lb, b + d) + g_shift);',
'  }',
'  return s * (255.0 / 9.0);',
'}',
'void try_(vec2 v, float extra){ float k = bic(v) + extra; if (k < g_bc) { g_bc = k; g_best = v; } }',
'vec2 fixA(vec2 p){ vec2 v = F(u_fab, p).xy; for (int i = 0; i < 2; i++) v = F(u_fab, p - u_t * v).xy; return v; }',
'vec2 fixB(vec2 p){ vec2 w = F(u_fba, p).xy; for (int i = 0; i < 2; i++) w = F(u_fba, p - (1.0 - u_t) * w).xy; return -w; }',
'void main(){',
'  g_inv = 1.0 / vec2(textureSize(u_la, 0));',
'  vec4 gm = texelFetch(u_gm, ivec2(0), 0);',
'  g_shift = gm.y - gm.x;',
'  vec2 p = gl_FragCoord.xy; g_p = p;',
'  g_bc = 1e9; g_best = vec2(0.0);',
'  try_(fixA(p), 0.0); try_(fixB(p), 0.0); try_(vec2(0.0), 0.5);',
'  try_(fixA(p + vec2(4.0, 0.0)), 0.0); try_(fixA(p - vec2(4.0, 0.0)), 0.0);',
'  try_(fixB(p + vec2(0.0, 4.0)), 0.0); try_(fixB(p - vec2(0.0, 4.0)), 0.0);',
'  o = vec4(g_best, g_bc, 0.0);',
'}'
].join('\n');

// Can the motion of this pair be read at all? Share of 576 sample points
// where the two flows agree (A→B then back lands within 1.5 px). Measured:
// a scene cut 0.09; a flash 0.18–0.35; the hardest same-scene pairs — fast
// camera turns, a crowd, trees passing a lens — never under 0.46. Below
// u_cutAt the composite cross-fades this pair instead. Out: (cost, agree, 0, flag).
var FS_VERDICT = HEAD + [
'uniform sampler2D u_fab, u_fba;',
'uniform float u_cutAt;',
'void main(){',
'  ivec2 s = textureSize(u_fab, 0);',
'  float c = 0.0, agree = 0.0;',
'  for (int y = 0; y < 18; y++) for (int x = 0; x < 32; x++) {',
'    vec2 p = (vec2(float(x), float(y)) + 0.5) * vec2(s) / vec2(32.0, 18.0);',
'    vec4 f = texelFetch(u_fab, ivec2(p), 0);',
'    agree += length(f.xy + F(u_fba, p + f.xy).xy) < 1.5 ? 1.0 : 0.0;',
'    c += f.z;',
'  }',
'  c /= 576.0; agree /= 576.0;',
'  o = vec4(c, agree, 0.0, agree < u_cutAt ? 1.0 : 0.0);',
'}'
].join('\n');

// Full-size composite. Of the 4 nearest motion samples, the ones whose two
// ends agree in colour weigh most (sharp moving edges); an end that falls
// outside the frame drops out (content entering at the edge comes from the
// side that has it). An unreadable pair — a cut, a flash — is cross-faded:
// a 1/30 s dissolve at a cut is invisible, and on a flash it is the answer.
var FS_COMP = HEAD + [
'uniform sampler2D u_a, u_b, u_tf, u_verdict;',
'uniform float u_t;',
'uniform vec2 u_out, u_scale;',   // output size; output px per working px
'float inside(vec2 uv){ vec2 m = step(vec2(0.0), uv) * step(uv, vec2(1.0)); return m.x * m.y; }',
'void main(){',
'  vec2 pf = gl_FragCoord.xy;',
'  if (texelFetch(u_verdict, ivec2(0), 0).w > 0.5) {',
'    vec2 uv0 = pf / u_out;',
'    o = vec4(mix(texture(u_a, uv0).rgb, texture(u_b, uv0).rgb, u_t), 1.0); return;',
'  }',
'  ivec2 s = textureSize(u_tf, 0);',
'  vec2 q = clamp(pf / u_scale - 0.5, vec2(0.0), vec2(s) - 1.0);',
'  ivec2 i0 = ivec2(floor(q)); vec2 f = q - vec2(i0);',
'  vec3 acc = vec3(0.0); float wsum = 0.0;',
'  for (int k = 0; k < 4; k++) {',
'    ivec2 o2 = ivec2(k & 1, k >> 1);',
'    vec2 v = texelFetch(u_tf, min(i0 + o2, s - 1), 0).xy * u_scale;',
'    float sw = (o2.x == 1 ? f.x : 1.0 - f.x) * (o2.y == 1 ? f.y : 1.0 - f.y) + 1e-3;',
'    vec2 ua = (pf - u_t * v) / u_out, ub = (pf + (1.0 - u_t) * v) / u_out;',
'    vec3 ca = texture(u_a, ua).rgb, cb = texture(u_b, ub).rgb;',
'    float wa = (1.0 - u_t) * inside(ua), wb = u_t * inside(ub);',
'    if (wa + wb <= 0.0) { wa = 1.0 - u_t; wb = u_t; }',
'    float agree = (wa > 0.0 && wb > 0.0) ? exp(-dot(ca - cb, ca - cb) * 40.0) : 0.35;',
'    float w = sw * (0.05 + agree);',
'    acc += w * (wa * ca + wb * cb) / (wa + wb); wsum += w;',
'  }',
'  o = vec4(acc / wsum, 1.0);',
'}'
].join('\n');

function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        var log = gl.getShaderInfoLog(s);
        gl.deleteShader(s);
        throw Object.assign(new Error('motion-interp shader: ' + log), { code: 'shader' });
    }
    return s;
}
function program(gl, fs, defs) {
    if (defs) {
        var lines = Object.keys(defs).map(function (k) { return '#define ' + k + ' ' + defs[k]; }).join('\n');
        fs = fs.replace('#version 300 es\n', '#version 300 es\n' + lines + '\n');
    }
    var p = gl.createProgram();
    var v = compile(gl, gl.VERTEX_SHADER, VS), f = compile(gl, gl.FRAGMENT_SHADER, fs);
    gl.attachShader(p, v); gl.attachShader(p, f);
    gl.bindAttribLocation(p, 0, 'a_pos');
    gl.linkProgram(p);
    gl.deleteShader(v); gl.deleteShader(f);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        throw Object.assign(new Error('motion-interp link: ' + gl.getProgramInfoLog(p)), { code: 'shader' });
    }
    var u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) { var info = gl.getActiveUniform(p, i); u[info.name] = gl.getUniformLocation(p, info.name); }
    return { p: p, u: u };
}

// Working area: ≈640×360 whatever the shape (a portrait clip must not get a
// bigger flow than a landscape one). 640 wide was measured against 480 (−0.8
// to −2.2 dB on fast motion) and 960 (no better, slower).
var WORK_PIXELS = 640 * 360;

function create(gl, opts) {
    opts = opts || {};
    var W = opts.width | 0, H = opts.height | 0;
    if (!(W > 0 && H > 0)) throw new Error('motion-interp: size');
    // Flow lives in float textures: RGBA32F / RG16F render targets need it.
    if (!gl.getExtension('EXT_color_buffer_float')) {
        throw Object.assign(new Error('motion-interp: no float render targets'), { code: 'no_float_rt' });
    }
    var sc = Math.max(1, Math.sqrt((W * H) / (opts.workPixels || WORK_PIXELS)));
    var workW = Math.max(16, Math.round(W / sc)), workH = Math.max(16, Math.round(H / sc));
    // λ — the price of a vector straying from its parent's, in grey levels
    // per pixel. Measured 0, 0.25, 0.5, 1, 2, 4, 12: 0.25 best or equal on
    // every clip; the first draft's 4 lost 3–5 dB.
    var lambda = 0.25;
    var cutAt = opts.cutAt != null ? opts.cutAt : 0.25;

    var P = {
        luma: program(gl, FS_LUMA), half: program(gl, FS_HALF), grad: program(gl, FS_GRAD),
        stats: program(gl, FS_STATS), bm: program(gl, FS_BM, { BM_TAPS: 4, BM_DESC: 3 }),
        dense: program(gl, FS_DENSE), tf: program(gl, FS_TFIELD),
        verdict: program(gl, FS_VERDICT), comp: program(gl, FS_COMP)
    };
    var vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    var vb = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    var owned = [];
    function target(w, h, kind) {
        var t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        if (kind === 'luma') gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, w, h, 0, gl.RED, gl.UNSIGNED_BYTE, null);
        else if (kind === 'grad') gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG16F, w, h, 0, gl.RG, gl.HALF_FLOAT, null);
        else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, w, h, 0, gl.RGBA, gl.FLOAT, null);
        var lin = kind === 'luma';
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, lin ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, lin ? gl.LINEAR : gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        var fb = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
        var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        if (!ok) throw Object.assign(new Error('motion-interp: framebuffer ' + kind), { code: 'no_float_rt' });
        var r = { tex: t, fb: fb, w: w, h: h };
        owned.push(r);
        return r;
    }

    var levels = [];
    for (var lw = workW, lh = workH; ; lw = Math.ceil(lw / 2), lh = Math.ceil(lh / 2)) {
        levels.push({ w: lw, h: lh });
        if (Math.min(lw, lh) < 40 || levels.length >= 6) break;
    }
    var top = levels.length - 1;
    var pyrA, pyrB, gradA, gradB, bmAB, bmBA, fAB, fBA, tField, gStats, gVerdict;
    try {
        pyrA = levels.map(function (l) { return target(l.w, l.h, 'luma'); });
        pyrB = levels.map(function (l) { return target(l.w, l.h, 'luma'); });
        gradA = target(workW, workH, 'grad'); gradB = target(workW, workH, 'grad');
        bmAB = levels.map(function (l) { return target(Math.ceil(l.w / 8), Math.ceil(l.h / 8), 'f'); });
        bmBA = levels.map(function (l) { return target(Math.ceil(l.w / 8), Math.ceil(l.h / 8), 'f'); });
        fAB = target(workW, workH, 'f'); fBA = target(workW, workH, 'f');
        tField = target(workW, workH, 'f');
        gStats = target(1, 1, 'f'); gVerdict = target(1, 1, 'f');
    } catch (e) { dispose(); throw e; }

    function draw(prog, dst, bind) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, dst ? dst.fb : null);
        gl.viewport(0, 0, dst ? dst.w : W, dst ? dst.h : H);
        gl.useProgram(prog.p);
        var unit = 0;
        bind(function tex(name, t) {
            gl.activeTexture(gl.TEXTURE0 + unit);
            gl.bindTexture(gl.TEXTURE_2D, t);
            gl.uniform1i(prog.u[name], unit);
            unit++;
        }, prog.u);
        gl.bindVertexArray(vao);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        gl.bindVertexArray(null);
        gl.activeTexture(gl.TEXTURE0);
    }
    function pyramid(src, pyr, grad) {
        draw(P.luma, pyr[0], function (tex, u) { tex('u_src', src); gl.uniform2f(u.u_work, workW, workH); });
        for (var i = 1; i < pyr.length; i++) {
            draw(P.half, pyr[i], function (tex) { tex('u_src', pyr[i - 1].tex); });
        }
        draw(P.grad, grad, function (tex) { tex('u_src', pyr[0].tex); });
    }
    function flow(pr, po, bms, out, dir, grad) {
        for (var i = top; i >= 0; i--) {
            draw(P.bm, bms[i], function (tex, u) {
                tex('u_ref', pr[i].tex); tex('u_oth', po[i].tex);
                tex('u_parent', i === top ? bms[i].tex : bms[i + 1].tex);
                tex('u_gm', gStats.tex);
                gl.uniform1i(u.u_coarsest, i === top ? 1 : 0);
                gl.uniform1i(u.u_radius, 7);
                gl.uniform1f(u.u_lambda, lambda);
                gl.uniform1f(u.u_dir, dir);
            });
        }
        draw(P.dense, out, function (tex, u) {
            tex('u_ref', pr[0].tex); tex('u_oth', po[0].tex); tex('u_blocks', bms[0].tex);
            tex('u_gm', gStats.tex); tex('u_grad', grad.tex);
            gl.uniform1f(u.u_lambda, lambda);
            gl.uniform1f(u.u_dir, dir);
        });
    }

    var curA = null, curB = null, pairs = 0, reused = 0;
    function prepare(texA, texB, aWasB) {
        if (aWasB === true && texA === curB && curB) {
            // the caller rolled A := B: B's pyramid and gradient are A's now
            var tp = pyrA; pyrA = pyrB; pyrB = tp;
            var tg = gradA; gradA = gradB; gradB = tg;
            reused++;
        } else {
            pyramid(texA, pyrA, gradA);
        }
        curA = texA; curB = texB;
        pyramid(texB, pyrB, gradB);
        draw(P.stats, gStats, function (tex) { tex('u_a', pyrA[top].tex); tex('u_b', pyrB[top].tex); });
        flow(pyrA, pyrB, bmAB, fAB, 1, gradA);
        flow(pyrB, pyrA, bmBA, fBA, -1, gradB);
        draw(P.verdict, gVerdict, function (tex, u) {
            tex('u_fab', fAB.tex); tex('u_fba', fBA.tex);
            gl.uniform1f(u.u_cutAt, cutAt);
        });
        pairs++;
    }
    function render(t, fb) {
        draw(P.tf, tField, function (tex, u) {
            tex('u_la', pyrA[0].tex); tex('u_lb', pyrB[0].tex); tex('u_fab', fAB.tex); tex('u_fba', fBA.tex);
            tex('u_gm', gStats.tex);
            gl.uniform1f(u.u_t, t);
        });
        draw(P.comp, fb ? { fb: fb, w: W, h: H } : null, function (tex, u) {
            tex('u_a', curA); tex('u_b', curB); tex('u_tf', tField.tex); tex('u_verdict', gVerdict.tex);
            gl.uniform1f(u.u_t, t);
            gl.uniform2f(u.u_out, W, H);
            gl.uniform2f(u.u_scale, W / workW, H / workH);
        });
    }
    // The last pair's verdict. readPixels waits for the GPU — call it rarely.
    function verdict() {
        var px = new Float32Array(4);
        gl.bindFramebuffer(gl.FRAMEBUFFER, gVerdict.fb);
        gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.FLOAT, px);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return { cost: px[0], agree: px[1], unreadable: px[3] > 0.5 };
    }
    function dispose() {
        owned.forEach(function (r) { try { gl.deleteTexture(r.tex); gl.deleteFramebuffer(r.fb); } catch (_) {} });
        owned = [];
        for (var k in P) { try { gl.deleteProgram(P[k].p); } catch (_) {} }
        try { gl.deleteVertexArray(vao); gl.deleteBuffer(vb); } catch (_) {}
    }
    return {
        prepare: prepare, render: render, verdict: verdict, dispose: dispose,
        info: function () { return { workW: workW, workH: workH, levels: levels.length, pairs: pairs, reused: reused }; },
        _programs: P
    };
}

root.MotionInterpGL = { create: create, WORK_PIXELS: WORK_PIXELS };
})(typeof self !== 'undefined' ? self : this);
